import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Email from '@material-ui/icons/EmailOutlined';
import LockOutline from '@material-ui/icons/LockOutlined';
import CustomInput from 'components/CustomInput/CustomInput';
import CustomPasswordInput from 'components/CustomInput/CustomPasswordInput';
import Button from 'components/CustomButtons/Button';
import CardBody from 'components/Card/CardBody';
import CardFooter from 'components/Card/CardFooter';
import { AuthConsumer } from 'components/Auth/authContext';
import CustomLink from 'components/CustomLink/CustomLink';
import { BodyM, DisplayS, HeadingLBold } from 'components/Typography/Typography';
import styles from 'assets/jss/material-dashboard-pro-react/views/loginPageStyle';
import ErrorBox from 'components/ErrorBox/ErrorBox';
import { unLoadQualtrics } from 'utils/qualtrics';
import { useSelector } from 'react-redux';
import { getLoginLabels, getPasswordRulesLabels } from 'redux/modules/dynamicLabels/selectors';
import OtpDialog from './common/OtpDialog';
import { dispatch } from 'redux/store';
import * as sessionActions from 'redux/modules/session/actions';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { AuthorizationService } from 'services';
import { finalize } from 'rxjs/operators';
import { setToast } from 'redux/modules/toast/actions';
import { translate } from 'components/Translation';
import _ from 'lodash';
import { getLoadingIndicator } from 'redux/modules/api/loadingIndicator/selector';

// @ts-ignore
const useStyles = makeStyles(styles);

export default function LoginPage() {
	const [otp, setOTP] = useState('');
	const [formData, setFormData] = useState({ user: '', secret: '' });

	const passwordRules = useSelector(getPasswordRulesLabels);
	const loginLabels = useSelector(getLoginLabels);
	const isLoading = useSelector(getLoadingIndicator);

	const setField = (name: string, value: string) => setFormData((data) => ({ ...data, [name]: value }));

	const checkValidity = () => {
		return isLoading || Object.keys(formData).every((key) => !!formData[key] && formData[key].length > 0);
	};

	const headerText = (
		<React.Fragment>
			<BodyM color="primary-light">{loginLabels?.welcome}</BodyM>
			<DisplayS color="brand">{loginLabels?.enterprise}</DisplayS>
		</React.Fragment>
	);

	const classes = useStyles();
	const loginUnsuccessfulText = (errorMessage: string) => (
		<div className={classes.loginUnsuccessful}>
			<ErrorBox text={errorMessage} />
		</div>
	);

	const requestForOtp = () => {
		dispatch(showLoadingIndicator());
		dispatch(sessionActions.setOTPWrong(false));
		dispatch(sessionActions.setErrorMessage({ errorCode: '', errorMessage: '' }));
		AuthorizationService.requestResetPasswordOtp(formData.user)
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
				})
			)
			.subscribe(
				() => {
					setToast({
						error: false,
						label: translate('otp.token.sent'),
					});
				},
				(error) => {
					// setServerErrorMessage(ErrorMapper.mapToMessage(error.response.code));
				}
			);
	};

	useEffect(() => {
		unLoadQualtrics();
	});

	return (
		<AuthConsumer>
			{({ handleAuthentication, isLoginSuccessful, checkingToken, handleValidateLoginWithOTP, ...session }) =>
				!checkingToken ? (
					<form
						onSubmit={(event) => {
							event.preventDefault();
							handleAuthentication(formData);
						}}
					>
						<CardBody className={classes.cardBody}>
							{isLoginSuccessful ? headerText : loginUnsuccessfulText(session.errorMessage)}
							<CustomInput
								labelText={loginLabels?.email}
								id="firstname"
								formControlProps={{
									fullWidth: true,
								}}
								error={!isLoginSuccessful}
								icon={<Email />}
								inputProps={{
									onChange: (event: React.FormEvent<HTMLInputElement>) =>
										setField('user', (event.target as HTMLInputElement).value),
									value: formData.user,
								}}
							/>
							<CustomPasswordInput
								labelText={loginLabels?.password}
								id="password"
								error={!isLoginSuccessful}
								formControlProps={{
									fullWidth: true,
								}}
								icon={<LockOutline />}
								inputProps={{
									onChange: (event: React.FormEvent<HTMLInputElement>) =>
										setField('secret', (event.target as HTMLInputElement).value),
									value: formData.secret,
									type: 'password',
									autoComplete: 'off',
								}}
							/>
							<br />
							<BodyM color="danger">{passwordRules.passwordAlert}</BodyM>
						</CardBody>
						<CardFooter className={classes.justifyContentCenter}>
							<>
								<Button
									color="primary-light"
									disabled={!checkValidity()}
									type="submit"
									id="login-button"
								>
									{loginLabels?.loginButton}
								</Button>
								<div className={classes.footerLinks}>
									<BodyM color="secondary-light">
										<>
											{loginLabels?.forgotPassword}
											<CustomLink to="/auth/forgot-page" color="brand" type="bodyMBold">
												{loginLabels?.reset}
											</CustomLink>
										</>
									</BodyM>
									<BodyM color="secondary-light">
										<>
											{loginLabels?.wantJoin}
											<CustomLink to="/register" color="brand" type="bodyMBold">
												{loginLabels?.register}
											</CustomLink>
										</>
									</BodyM>
								</div>
							</>
						</CardFooter>
						<OtpDialog
							key={`LOGIN_OTP_${session.OTPModalOpen}`}
							otp={otp}
							showOTPNote
							setOtp={setOTP}
							sendOtp={requestForOtp}
							error={session.errorMessage}
							isOpen={session.OTPModalOpen}
							heading={loginLabels?.mfa_login}
							isWrongOtp={session.isWrongOTP}
							loading={session.isValidatingOTP}
							onSubmit={() => handleValidateLoginWithOTP(formData.user, otp)}
							onClose={() => dispatch(sessionActions.setOTPModalOpen(false))}
							setIsWrongOtp={(state) => dispatch(sessionActions.setOTPWrong(state))}
							{...(session.errorCode === 'OTP_FAIL' && { showResendOption: true })}
						/>
					</form>
				) : (
					<CardBody className={classes.cardBody}>
						<HeadingLBold color="brand">{loginLabels?.checkingTitle}</HeadingLBold>
						<BodyM color="secondary-light">{loginLabels?.checkingBody}</BodyM>
					</CardBody>
				)
			}
		</AuthConsumer>
	);
}
